import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { listCollections, listDocuments, deleteDocument, deleteAllDocuments, searchCollection, getCollectionDetails, previewDocument } from '../../services/adminApi';
import { notify, confirmAsync } from '../../utils/notify';
import './CollectionBrowser.css';

function CollectionBrowser({ onCollectionSelect, selectedCollection: externalSelectedCollection }) {
  const location = useLocation();
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState(externalSelectedCollection || null);
  const [collectionDetails, setCollectionDetails] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const searchAbortController = useRef(null);
  const prevLocationRef = useRef(location.pathname);

  const loadCollections = useCallback(async () => {
    try {
      setLoading(true);
      const response = await listCollections();
      setCollections(response.data);
      if (response.data.length > 0 && !selectedCollection) {
        setSelectedCollection(response.data[0].name);
      }
    } catch (err) {
      console.error('Failed to load collections:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedCollection]);

  useEffect(() => {
    loadCollections();
  }, [loadCollections]);

  useEffect(() => {
    if (externalSelectedCollection !== undefined) {
      setSelectedCollection(externalSelectedCollection);
    }
  }, [externalSelectedCollection]);

  useEffect(() => {
    if (selectedCollection) {
      loadDocuments(selectedCollection);
      loadCollectionDetails(selectedCollection);
      clearSearch();
      if (onCollectionSelect) {
        onCollectionSelect(selectedCollection);
      }
    }
  }, [selectedCollection, onCollectionSelect]);

  // Clear search when navigating away from knowledge base page
  useEffect(() => {
    const currentPath = location.pathname;
    const prevPath = prevLocationRef.current;
    
    // If we were on qdrant page and now we're not, clear search
    // This handles the case when navigating to another menu item
    if (prevPath.includes('/admin/qdrant') && !currentPath.includes('/admin/qdrant')) {
      // Cancel any ongoing search
      if (searchAbortController.current) {
        searchAbortController.current.abort();
        searchAbortController.current = null;
      }
      setSearchResults(null);
      setSearchQuery('');
      setSearching(false);
    }
    
    // Update ref for next comparison
    prevLocationRef.current = currentPath;
  }, [location.pathname]);
  
  // Clear search when component unmounts (user navigates away)
  useEffect(() => {
    return () => {
      // Cancel any ongoing search and clear results when component unmounts
      if (searchAbortController.current) {
        searchAbortController.current.abort();
        searchAbortController.current = null;
      }
    };
  }, []);

  const loadDocuments = async (collectionName) => {
    try {
      setLoading(true);
      const response = await listDocuments(collectionName);
      setDocuments(response.data);
    } catch (err) {
      console.error('Failed to load documents:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadCollectionDetails = async (collectionName) => {
    try {
      const response = await getCollectionDetails(collectionName);
      setCollectionDetails(response.data);
    } catch (err) {
      console.error('Failed to load collection details:', err);
    }
  };

  const clearSearch = () => {
    // Cancel any ongoing search
    if (searchAbortController.current) {
      searchAbortController.current.abort();
      searchAbortController.current = null;
    }
    setSearchResults(null);
    setSearchQuery('');
    setSearching(false);
  };

  const cancelSearch = () => {
    clearSearch();
  };

  const handleSearch = async () => {
    if (!searchQuery.trim() || !selectedCollection) {
      notify.warning('Please enter a search query');
      return;
    }

    // Cancel any previous search
    if (searchAbortController.current) {
      searchAbortController.current.abort();
    }

    // Create new abort controller for this search
    searchAbortController.current = new AbortController();

    try {
      setSearching(true);
      const response = await searchCollection(
        selectedCollection, 
        searchQuery.trim(), 
        10,
        searchAbortController.current.signal
      );
      
      // Check if request was aborted
      if (searchAbortController.current?.signal.aborted) {
        return;
      }
      
      if (response.data) {
        setSearchResults(response.data);
        if (response.data.count === 0) {
          console.log('No results found for query:', searchQuery);
        }
      } else {
        setSearchResults({ query: searchQuery, results: [], count: 0 });
      }
    } catch (err) {
      // Don't show error if search was cancelled
      if (err.name === 'AbortError' || searchAbortController.current?.signal.aborted) {
        return;
      }
      console.error('Failed to search collection:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Search failed. Please try again.';
      notify.error('Search failed: ' + errorMessage);
      setSearchResults(null);
    } finally {
      if (!searchAbortController.current?.signal.aborted) {
        setSearching(false);
        searchAbortController.current = null;
      }
    }
  };

  const handleDelete = async (docId) => {
    if (!(await confirmAsync({ message: 'Are you sure you want to delete this document?', danger: true }))) {
      return;
    }

    try {
      await deleteDocument(selectedCollection, docId);
      loadDocuments(selectedCollection);
      loadCollections();
    } catch (err) {
      notify.error('Failed to delete document');
    }
  };


  const handlePreview = async (docId) => {
    try {
      setPreviewLoading(true);
      const response = await previewDocument(selectedCollection, docId);
      setPreviewDoc(response.data);
    } catch (err) {
      notify.error('Failed to load document preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  return (
    <div className="collection-browser">
      <div className="collections-sidebar">
        <h3>Collections</h3>
        {loading && collections.length === 0 ? (
          <div className="loading">Loading collections...</div>
        ) : (
          collections.map((col) => (
            <button
              key={col.name}
              className={`collection-item ${selectedCollection === col.name ? 'active' : ''}`}
              onClick={() => setSelectedCollection(col.name)}
            >
              <div className="collection-name">{col.name}</div>
              <div className="collection-stats">
                <span>{col.document_count} docs</span>
                <span>{col.vector_count} vectors</span>
                {col.storage_usage && <span>{formatFileSize(col.storage_usage)}</span>}
              </div>
            </button>
          ))
        )}
      </div>

      <div className="documents-content">
        {selectedCollection && (
          <>
            <div className="collection-header">
              <h3>Documents in {selectedCollection}</h3>
              {collectionDetails && (
                <div className="collection-details">
                  <span>{collectionDetails.document_count} documents</span>
                  <span>{collectionDetails.vector_count} vectors</span>
                  {collectionDetails.storage_usage && (
                    <span>Storage: {formatFileSize(collectionDetails.storage_usage)}</span>
                  )}
                  {collectionDetails.last_updated && (
                    <span>Updated: {new Date(collectionDetails.last_updated).toLocaleDateString()}</span>
                  )}
                </div>
              )}
            </div>

            <div className="search-section">
              <div className="search-box">
                <input
                  type="text"
                  placeholder="Search in collection..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && !searching && handleSearch()}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      clearSearch();
                    }
                  }}
                  aria-label="Search query input for collection"
                  disabled={searching}
                />
                {searching ? (
                  <button
                    className="cancel-search-button"
                    onClick={cancelSearch}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        cancelSearch();
                      }
                    }}
                    aria-label="Cancel search"
                  >
                    <span className="search-loading-spinner"></span>
                    Searching...
                  </button>
                ) : (
                  <button
                    className="search-button"
                    onClick={handleSearch}
                    disabled={!searchQuery.trim()}
                    onKeyDown={(e) => {
                      if ((e.key === 'Enter' || e.key === ' ') && searchQuery.trim()) {
                        e.preventDefault();
                        handleSearch();
                      }
                    }}
                    aria-label="Search in collection"
                  >
                    Search
                  </button>
                )}
                {searchResults && !searching && (
                  <button
                    className="clear-search-button"
                    onClick={clearSearch}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        clearSearch();
                      }
                    }}
                    aria-label="Clear search results"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            {searchResults ? (
              <div className="search-results">
                <h4>Search Results ({searchResults.count} found)</h4>
                {searchResults.results && searchResults.results.length > 0 ? (
                  <div className="search-results-list">
                    {searchResults.results.map((result, idx) => (
                      <div key={idx} className="search-result-item">
                        <div className="result-score">
                          Score: {result.score ? (result.score * 100).toFixed(2) : '0.00'}%
                          {result.keyword_boost && result.keyword_boost > 0 && (
                            <span className="keyword-boost-indicator" title="Boosted by keyword match">
                              {' '}(+{(result.keyword_boost * 100).toFixed(1)}% keyword boost)
                            </span>
                          )}
                        </div>
                        <div className="result-text">{result.text || '(No text content)'}</div>
                        {result.metadata && Object.keys(result.metadata).length > 0 && (
                          <div className="result-metadata">
                            {Object.entries(result.metadata).map(([key, value]) => (
                              <span key={key} className="metadata-tag">
                                {key}: {String(value)}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-results">
                    No results found for "{searchResults.query || searchQuery}". 
                    Try a different search term or check if the collection has documents.
                  </div>
                )}
              </div>
            ) : (
              <>
                {loading ? (
                  <div className="loading">Loading documents...</div>
                ) : documents.length > 0 ? (
                  <div className="documents-list">
                    {documents.map((doc) => (
                      <div key={doc.id} className="document-item">
                        <div className="document-info">
                          <div className="document-title">{doc.title || doc.original_filename}</div>
                          <div className="document-meta">
                            <span>Size: {formatFileSize(doc.file_size)}</span>
                            <span>Chunks: {doc.chunk_count || 0}</span>
                            <span>Uploaded: {new Date(doc.created_at).toLocaleDateString()}</span>
                          </div>
                          {doc.description && (
                            <div className="document-description">{doc.description}</div>
                          )}
                          {doc.tags && doc.tags.length > 0 && (
                            <div className="document-tags">
                              {doc.tags.map((tag, idx) => (
                                <span key={idx} className="tag">{tag}</span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="document-actions">
                          <button
                            className="preview-button"
                            onClick={() => handlePreview(doc.id)}
                          >
                            Preview
                          </button>
                          <button
                            className="delete-button"
                            onClick={() => handleDelete(doc.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-documents">No documents in this collection</div>
                )}
              </>
            )}
          </>
        )}
      </div>

      {previewDoc && (
        <div className="preview-modal">
          <div className="preview-modal-content">
            <div className="preview-header">
              <h3>{previewDoc.title || previewDoc.filename}</h3>
              <button className="close-button" onClick={() => setPreviewDoc(null)}>×</button>
            </div>
            <div className="preview-body">
              {previewLoading ? (
                <div>Loading preview...</div>
              ) : (
                <>
                  {previewDoc.description && (
                    <div className="preview-description">
                      <strong>Description:</strong> {previewDoc.description}
                    </div>
                  )}
                  {previewDoc.tags && previewDoc.tags.length > 0 && (
                    <div className="preview-tags">
                      <strong>Tags:</strong> {previewDoc.tags.join(', ')}
                    </div>
                  )}
                  <div className="preview-text">
                    <strong>Content Preview:</strong>
                    <pre>{previewDoc.preview_text}</pre>
                    {previewDoc.full_text_available && (
                      <div className="preview-note">
                        Note: This is a preview. Full document contains {previewDoc.chunk_count} chunks.
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CollectionBrowser;

